"use client";

import { useEffect, useState } from "react";
import { showToast } from "@/lib/toast";
import { useRoleStore } from "@/stores/roleStore";
import { roleService } from "@/services/roleService";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ModalEditPermissionProps {
  isOpen: boolean;
  onClose: () => void;
  isAdd: boolean;
  onComplete: () => void;
  permissionData: any;
}

// Role permission structure
interface RolePermission {
  read: boolean;
  create: boolean;
  delete: boolean;
  update: boolean;
}

interface RolesStructure {
  user: RolePermission;
  role: RolePermission;
  category: RolePermission;
  information: RolePermission;
  article: RolePermission;
  image: RolePermission;
  page: RolePermission;
  blog_post: RolePermission;
  menu: RolePermission;
  film_list: RolePermission;
  author: RolePermission;
  robot_txt: RolePermission;
  widget: RolePermission;
  comment: RolePermission;
  log: RolePermission;
}

// Default role structure based on new PermissionSubject enum
const defaultRole: RolesStructure = {
  user: { read: false, create: false, delete: false, update: false },
  role: { read: false, create: false, delete: false, update: false },
  category: { read: false, create: false, delete: false, update: false },
  information: { read: false, create: false, delete: false, update: false },
  article: { read: false, create: false, delete: false, update: false },
  image: { read: false, create: false, delete: false, update: false },
  page: { read: false, create: false, delete: false, update: false },
  blog_post: { read: false, create: false, delete: false, update: false },
  menu: { read: false, create: false, delete: false, update: false },
  film_list: { read: false, create: false, delete: false, update: false },
  author: { read: false, create: false, delete: false, update: false },
  robot_txt: { read: false, create: false, delete: false, update: false },
  widget: { read: false, create: false, delete: false, update: false },
  comment: { read: false, create: false, delete: false, update: false },
  log: { read: false, create: false, delete: false, update: false },
};

// Title mapping for roles
const titleRole = {
  user: "Người dùng",
  role: "Quyền hạn",
  category: "Danh mục",
  information: "Thông tin",
  article: "Bài viết",
  image: "Hình ảnh",
  page: "Trang",
  blog_post: "Blog Post",
  menu: "Menu",
  film_list: "Danh sách phim",
  author: "Tác giả",
  robot_txt: "Robot.txt",
  widget: "Widget",
  comment: "Bình luận",
  log: "Log",
};

const convertRolesObjectToArray = (rolesObject: any) => {
  const rolesArray: any = {};

  // Xử lý permissions bình thường
  for (const [key, value] of Object.entries(rolesObject)) {
    rolesArray[key] = Object.keys(value as any).filter(
      (permission) => (value as any)[permission]
    );
  }
  return { ...rolesArray, all: [] };
};

function ModalEditPermission({
  isOpen,
  onClose,
  isAdd,
  permissionData,
  onComplete,
}: ModalEditPermissionProps) {
  const { isCreating, isUpdating, createPermissionRole, updatePermissionRole } =
    useRoleStore();
  const [roles, setRoles] = useState<RolesStructure>(
    JSON.parse(JSON.stringify(defaultRole))
  );
  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");

  const isLoading = isCreating || isUpdating;

  const handleOnComplete = () => {
    if (isLoading) return;
    onClose();
    clearForm();
  };

  const clearForm = () => {
    setRoleName("");
    setRoleNameError("");
    setRoles(JSON.parse(JSON.stringify(defaultRole)));
  };

  useEffect(() => {
    if (!isAdd && isOpen && permissionData?._id) {
      handleGetDetail();
    } else if (isAdd && isOpen) {
      clearForm();
    }
  }, [isOpen, isAdd, permissionData?._id]);

  const handleGetDetail = async () => {
    if (!permissionData?._id || !isOpen) return;
    try {
      // Fetch permissions for this role
      const res: any = await roleService.getPermissionsByRoleId(
        permissionData._id
      );
      const rolePermissions = res?.data?.data?.data;
      const newRoles = JSON.parse(JSON.stringify(defaultRole));
      if (rolePermissions && rolePermissions.length > 0) {
        // Kiểm tra xem có phải admin không (subject: "all", action: ["manage"])
        const isAdmin = rolePermissions.some(
          (permission: any) =>
            permission.subject === "all" &&
            permission.action?.includes("manage")
        );

        if (isAdmin) {
          // Nếu là admin, set tất cả permissions = true
          Object.keys(newRoles).forEach((subject) => {
            newRoles[subject as keyof typeof newRoles] = {
              read: true,
              create: true,
              delete: true,
              update: true,
            };
          });
        } else {
          // Xử lý permissions bình thường
          for (const permission of rolePermissions) {
            const { subject, action } = permission;

            // Bỏ qua các subject không có trong defaultRole
            if (!newRoles[subject as keyof typeof newRoles]) {
              continue;
            }

            // Nếu action là mảng rỗng, bỏ qua
            if (!action || action.length === 0) {
              continue;
            }

            // Xử lý từng action trong mảng
            action.forEach((act: string) => {
              // Kiểm tra action có tồn tại trong role structure không
              if (
                (newRoles[subject as keyof typeof newRoles] as any)[act] !==
                undefined
              ) {
                (newRoles[subject as keyof typeof newRoles] as any)[act] = true;
              }
            });
          }
        }
      }

      setRoles(newRoles);
      setRoleName(permissionData?.name || "");
    } catch (error) {
      showToast.error(
        "Lỗi",
        "Không thể lấy chi tiết quyền hạn, vui lòng thử lại sau!"
      );
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleName.trim()) {
      setRoleNameError("Tên vai trò không được để trống");
      return;
    } else {
      setRoleNameError("");
    }

    const permissionPayload = {
      role: roleName,
      permission: convertRolesObjectToArray(roles),
    };

    try {
      if (isAdd) {
        const success = await createPermissionRole(permissionPayload);
        if (success) {
          showToast.success("Thành công", "Thêm mới vai trò thành công");
          onComplete();
          onClose();
        } else {
          showToast.error("Lỗi", "Không thể tạo vai trò mới");
        }
      } else {
        const success = await updatePermissionRole(
          permissionData._id,
          permissionPayload
        );
        if (success) {
          showToast.success("Thành công", "Cập nhật vai trò thành công");
          onComplete();
          onClose();
        } else {
          showToast.error("Lỗi", "Không thể cập nhật vai trò");
        }
      }
    } catch (error) {
      showToast.error(
        "Lỗi",
        `${
          isAdd ? "Thêm mới" : "Cập nhật"
        } vai trò thất bại, vui lòng thử lại sau`
      );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white uppercase">
            {isAdd ? "Thêm mới" : "Chỉnh sửa"} vai trò
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmitForm} className="flex flex-col h-[80vh]">
          <div className="flex-1 h-[80vh] p-6 overflow-y-auto">
            <div className="flex flex-col gap-6">
              <div className="px-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <Label className="text-sm font-semibold text-gray-900 dark:text-white">
                      Tên Vai trò
                    </Label>
                    <span className="text-sm text-red-600">(*)</span>
                  </div>
                  <Input
                    name="role-name"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    type="text"
                    placeholder="Nhập tên vai trò..."
                    className={roleNameError ? "border-red-500" : ""}
                  />
                  {roleNameError && (
                    <p className="text-sm text-red-600">{roleNameError}</p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="w-full flex flex-row justify-between items-center px-2 py-3">
                  <p className="font-bold text-gray-900 truncate w-[40%]">
                    Tên phân quyền
                  </p>
                  <div className="flex flex-row justify-between items-center w-[60%]">
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Xem
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Thêm
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Xóa
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Sửa
                    </div>
                    <div className="text-sm w-[20%] flex justify-center items-center font-bold text-gray-900">
                      Tất cả
                    </div>
                  </div>
                </div>
              </div>

              {isLoading && !isAdd ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Đang tải...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.keys(roles).map((role, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <div className="w-full flex flex-row justify-between items-center">
                        <p className="font-medium text-gray-900 truncate w-[40%] text-sm">
                          {titleRole[role as keyof typeof titleRole]}
                        </p>
                        <div className="flex flex-row justify-between items-center w-[60%]">
                          <div className="w-[20%] flex justify-center items-center">
                            <Checkbox
                              checked={roles[role as keyof typeof roles].read}
                              onCheckedChange={(checked: any) => {
                                setRoles((prevRoles: RolesStructure) => ({
                                  ...prevRoles,
                                  [role]: {
                                    ...prevRoles[
                                      role as keyof typeof prevRoles
                                    ],
                                    read: checked as boolean,
                                  },
                                }));
                              }}
                              className="w-5 h-5"
                            />
                          </div>
                          <div className="w-[20%] flex justify-center items-center">
                            <Checkbox
                              checked={roles[role as keyof typeof roles].create}
                              onCheckedChange={(checked: any) => {
                                setRoles((prevRoles: RolesStructure) => ({
                                  ...prevRoles,
                                  [role]: {
                                    ...prevRoles[
                                      role as keyof typeof prevRoles
                                    ],
                                    create: checked as boolean,
                                  },
                                }));
                              }}
                              className="w-5 h-5"
                            />
                          </div>
                          <div className="w-[20%] flex justify-center items-center">
                            <Checkbox
                              checked={roles[role as keyof typeof roles].delete}
                              onCheckedChange={(checked: any) => {
                                setRoles((prevRoles: RolesStructure) => ({
                                  ...prevRoles,
                                  [role]: {
                                    ...prevRoles[
                                      role as keyof typeof prevRoles
                                    ],
                                    delete: checked as boolean,
                                  },
                                }));
                              }}
                              className="w-5 h-5"
                            />
                          </div>
                          <div className="w-[20%] flex justify-center items-center">
                            <Checkbox
                              checked={roles[role as keyof typeof roles].update}
                              onCheckedChange={(checked: any) => {
                                setRoles((prevRoles: RolesStructure) => ({
                                  ...prevRoles,
                                  [role]: {
                                    ...prevRoles[
                                      role as keyof typeof prevRoles
                                    ],
                                    update: checked as boolean,
                                  },
                                }));
                              }}
                              className="w-5 h-5"
                            />
                          </div>
                          <div className="w-[20%] flex justify-center items-center">
                            <Checkbox
                              checked={
                                roles[role as keyof typeof roles].create &&
                                roles[role as keyof typeof roles].read &&
                                roles[role as keyof typeof roles].update &&
                                roles[role as keyof typeof roles].delete
                              }
                              onCheckedChange={(checked: any) => {
                                const newValue = checked as boolean;
                                setRoles((prevRoles: RolesStructure) => ({
                                  ...prevRoles,
                                  [role]: {
                                    create: newValue,
                                    read: newValue,
                                    update: newValue,
                                    delete: newValue,
                                  },
                                }));
                              }}
                              className="w-5 h-5"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleOnComplete}
              disabled={isLoading}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Đang xử lý..." : "Xác nhận"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ModalEditPermission;
